import React, { useState, useEffect, useCallback } from 'react';
import AdminSidebar from '../Sidebar/AdminSidebar';
import AdminNavbar from '../Navbars/AdminNavbar';
import { AxiosResponse } from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SuperAdminApis } from '../../apis/superAdminApi';
import { Oval } from 'react-loader-spinner';
import * as XLSX from 'xlsx';

const formatCurrency = (amount: any) => {
    const num = Number(amount);
    return isNaN(num) ? '₦0.00' : `₦${num.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (date: string | null) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-NG', {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const statusConfig: { [key: string]: { bg: string; text: string; label: string } } = {
    pending_confirmation: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending Confirmation' },
    confirmed: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Confirmed' },
    approved: { bg: 'bg-green-100', text: 'text-green-800', label: 'Approved' },
    disputed: { bg: 'bg-red-100', text: 'text-red-800', label: 'Disputed' },
    cancelled: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Cancelled' },
};

const getStatusBadge = (status: string) => {
    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-700', label: status };
    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-[600] ${config.bg} ${config.text}`}>
            {config.label}
        </span>
    );
};

function DeliveryPayments() {
    const [payments, setPayments] = useState<any>(null);
    const [stats, setStats] = useState<any>(null);
    const [loader, setLoader] = useState(false);
    const [statsLoader, setStatsLoader] = useState(false);

    // Filters
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [rateType, setRateType] = useState('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [perPage] = useState(20);

    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    const handleSelect = (id: number, checked: boolean) => {
        if (checked) {
            setSelectedIds(prev => [...prev, id]);
        } else {
            setSelectedIds(prev => prev.filter(i => i !== id));
        }
    };

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            const allIds = payments?.data?.map((p: any) => p.id) || [];
            setSelectedIds(allIds);
        } else {
            setSelectedIds([]);
        }
    };

    const exportSelected = () => {
        if (selectedIds.length === 0) {
            toast.warning('Please select at least one payment to export');
            return;
        }
        const selected = payments?.data?.filter((p: any) => selectedIds.includes(p.id)) || [];
        doExport(selected);
        toast.success(`Exported ${selected.length} selected payments!`);
    };

    const fetchPayments = useCallback(async (page = 1) => {
        setLoader(true);
        try {
            const response: AxiosResponse<any> = await SuperAdminApis.getDeliveryPayments({
                search,
                status: statusFilter,
                rate_type: rateType,
                start_date: startDate,
                end_date: endDate,
                per_page: perPage,
                page,
            });
            if (response?.data?.status) {
                setPayments(response.data.data);
            }
        } catch (error: any) {
            console.error('Error fetching payments:', error);
            toast.error('Failed to load delivery payments');
        } finally {
            setLoader(false);
        }
    }, [search, statusFilter, rateType, startDate, endDate, perPage]);

    const fetchStats = useCallback(async () => {
        setStatsLoader(true);
        try {
            const response: AxiosResponse<any> = await SuperAdminApis.getDeliveryPaymentStats({
                start_date: startDate,
                end_date: endDate,
            });
            if (response?.data?.status) {
                setStats(response.data.data);
            }
        } catch (error: any) {
            console.error('Error fetching stats:', error);
        } finally {
            setStatsLoader(false);
        }
    }, [startDate, endDate]);

    useEffect(() => {
        fetchPayments();
        fetchStats();
    }, []);

    const handleSearch = () => {
        fetchPayments(1);
        fetchStats();
        setSelectedIds([]);
    };

    const handleClearFilters = () => {
        setSearch('');
        setStatusFilter('all');
        setRateType('all');
        setStartDate('');
        setEndDate('');
        setSelectedIds([]);
        setTimeout(() => {
            fetchPayments(1);
            fetchStats();
        }, 0);
    };

    const paginator = (page: number) => {
        fetchPayments(page);
    };

    const doExport = (data: any[]) => {
        const exportData = data.map((p: any, i: number) => ({
            'S/N': i + 1,
            'Shipment Number': p.shipment_number || '—',
            'Rider': p.rider_first_name ? `${p.rider_first_name} ${p.rider_last_name || ''}` : '—',
            'Rider Email': p.rider_email || '—',
            'Fleet': p.fleet_first_name ? `${p.fleet_first_name} ${p.fleet_last_name || ''}` : '—',
            'Amount': Number(p.amount || 0),
            'Gross Amount': Number(p.gross_amount || p.amount || 0),
            'Rate Type': p.rate_type || '—',
            'Rider Town': p.rider_town || '—',
            'Status': statusConfig[p.status]?.label || p.status,
            'Late Fine': Number(p.fine_amount || p.late_fine_amount || 0),
            'Fine Days': Number(p.fine_days || 0),
            'Fine Reason': p.late_fine_reason || '—',
            'Confirmed By': p.confirmed_by_first_name ? `${p.confirmed_by_first_name} ${p.confirmed_by_last_name || ''}` : '—',
            'Confirmed By Email': p.confirmed_by_email || '—',
            'Confirmed At': formatDate(p.confirmed_at),
            'Approved By': p.approved_by_first_name ? `${p.approved_by_first_name} ${p.approved_by_last_name || ''}` : '—',
            'Approved By Email': p.approved_by_email || '—',
            'Approved At': formatDate(p.approved_at),
            'Created At': formatDate(p.created_at),
        }));

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(exportData);
        ws['!cols'] = [
            { wch: 5 }, { wch: 18 }, { wch: 22 }, { wch: 25 }, { wch: 22 },
            { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 15 }, { wch: 20 },
            { wch: 12 }, { wch: 8 }, { wch: 25 },
            { wch: 22 }, { wch: 25 }, { wch: 20 },
            { wch: 22 }, { wch: 25 }, { wch: 20 },
            { wch: 20 },
        ];
        XLSX.utils.book_append_sheet(wb, ws, 'Delivery Payments');
        XLSX.writeFile(wb, `delivery_payments_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const exportToExcel = () => {
        if (!payments?.data || payments.data.length === 0) {
            toast.warning('No data to export');
            return;
        }
        doExport(payments.data);
        toast.success('Export completed!');
    };

    return (
        <>
            <AdminSidebar title="Delivery Payments" />
            <div className="relative md:ml-64 bg-[#FAFAFA]">
                <AdminNavbar title="Delivery Payments" />
                <div className="lg:px-10 pb-6 px-6 bg-[#FAFAFA] min-h-screen">

                    {/* Stats Cards */}
                    {stats && (
                        <>
                            {/* Row 1 */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                                <div className="bg-white rounded-[10px] p-3 text-center shadow-sm border">
                                    <p className="text-[22px] font-[700] text-[#333333]">{stats.total_payments}</p>
                                    <p className="text-[11px] text-[#808080]">Total Payments</p>
                                    <p className="text-[12px] font-[600] text-[#333333] mt-1">{formatCurrency(stats.total_amount)}</p>
                                </div>
                                <div className="bg-green-50 rounded-[10px] p-3 text-center shadow-sm border border-green-200">
                                    <p className="text-[22px] font-[700] text-green-700">{stats.credited_count ?? 0}</p>
                                    <p className="text-[11px] text-green-600">Total Credited</p>
                                    <p className="text-[12px] font-[600] text-green-700 mt-1">{formatCurrency(stats.credited_amount ?? 0)}</p>
                                </div>
                                <div className="bg-yellow-50 rounded-[10px] p-3 text-center shadow-sm border border-yellow-200">
                                    <p className="text-[22px] font-[700] text-yellow-700">{stats.pending_count}</p>
                                    <p className="text-[11px] text-yellow-600">Total Pending</p>
                                    <p className="text-[12px] font-[600] text-yellow-700 mt-1">{formatCurrency(stats.pending_amount)}</p>
                                </div>
                                <div className="bg-blue-50 rounded-[10px] p-3 text-center shadow-sm border border-blue-200">
                                    <p className="text-[22px] font-[700] text-blue-700">{stats.confirmed_count}</p>
                                    <p className="text-[11px] text-blue-600">Total Confirmed</p>
                                    <p className="text-[12px] font-[600] text-blue-700 mt-1">{formatCurrency(stats.confirmed_amount)}</p>
                                </div>
                            </div>

                            {/* Row 2 */}
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                                <div className="bg-teal-50 rounded-[10px] p-3 text-center shadow-sm border border-teal-200">
                                    <p className="text-[22px] font-[700] text-teal-700">{stats.approved_count}</p>
                                    <p className="text-[11px] text-teal-600">Total Approved</p>
                                    <p className="text-[12px] font-[600] text-teal-700 mt-1">{formatCurrency(stats.approved_amount)}</p>
                                </div>
                                <div className="bg-red-50 rounded-[10px] p-3 text-center shadow-sm border border-red-200">
                                    <p className="text-[22px] font-[700] text-red-700">{stats.disputed_count}</p>
                                    <p className="text-[11px] text-red-600">Total Disputed</p>
                                    <p className="text-[12px] font-[600] text-red-700 mt-1">{formatCurrency(stats.disputed_amount)}</p>
                                </div>
                                <div className="bg-purple-50 rounded-[10px] p-3 text-center shadow-sm border border-purple-200">
                                    <div className="flex justify-between text-[11px]">
                                        <span className="text-purple-600">Metro: {stats.metro_count}</span>
                                        <span className="text-indigo-600">Other: {stats.other_count}</span>
                                    </div>
                                    <p className="text-[12px] font-[600] text-purple-700 mt-1">{formatCurrency(stats.metro_amount)}</p>
                                    <p className="text-[12px] font-[600] text-indigo-700">{formatCurrency(stats.other_amount)}</p>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Filters */}
                    <div className="bg-white rounded-[10px] p-4 shadow-sm border mb-5">
                        <div className="flex flex-wrap gap-3 items-end">
                            <div className="flex-1 min-w-[180px]">
                                <label className="block text-[12px] text-[#808080] mb-1">Search</label>
                                <input
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Shipment #, rider name, email..."
                                    className="border border-gray-300 text-sm rounded-md block w-full p-2 text-gray-600"
                                />
                            </div>
                            <div className="min-w-[140px]">
                                <label className="block text-[12px] text-[#808080] mb-1">Status</label>
                                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-gray-300 text-sm rounded-md block w-full p-2 text-gray-600">
                                    <option value="all">All Status</option>
                                    <option value="pending_confirmation">Pending</option>
                                    <option value="confirmed">Confirmed</option>
                                    <option value="credited">Approved</option>
                                    <option value="disputed">Disputed</option>
                                    <option value="rejected">Rejected</option>
                                </select>
                            </div>
                            <div className="min-w-[120px]">
                                <label className="block text-[12px] text-[#808080] mb-1">Rate Type</label>
                                <select value={rateType} onChange={(e) => setRateType(e.target.value)} className="border border-gray-300 text-sm rounded-md block w-full p-2 text-gray-600">
                                    <option value="all">All Types</option>
                                    <option value="metro">Metro</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                            <div className="min-w-[140px]">
                                <label className="block text-[12px] text-[#808080] mb-1">Start Date</label>
                                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="border border-gray-300 text-sm rounded-md block w-full p-2 text-gray-600" />
                            </div>
                            <div className="min-w-[140px]">
                                <label className="block text-[12px] text-[#808080] mb-1">End Date</label>
                                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="border border-gray-300 text-sm rounded-md block w-full p-2 text-gray-600" />
                            </div>
                            <div className="flex gap-2">
                                <button onClick={handleSearch} className="bg-[#E04343] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-600">Search</button>
                                <button onClick={handleClearFilters} className="bg-gray-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-600">Clear</button>
                                <button onClick={exportToExcel} disabled={!payments?.data?.length} className="bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50">Export All</button>
                            </div>
                        </div>
                    </div>

                    {/* Selection Banner */}
                    {selectedIds.length > 0 && (
                        <div className="bg-purple-50 border border-purple-200 rounded-[10px] p-3 mb-4 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <span className="text-sm font-medium text-purple-800">
                                    {selectedIds.length} payment{selectedIds.length > 1 ? 's' : ''} selected
                                </span>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={exportSelected}
                                    className="bg-purple-600 text-white px-4 py-1.5 rounded-md text-sm font-medium hover:bg-purple-700"
                                >
                                    Export Selected ({selectedIds.length})
                                </button>
                                <button
                                    onClick={() => setSelectedIds([])}
                                    className="text-sm text-purple-600 hover:text-purple-800 font-medium px-2"
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Table */}
                    {loader ? (
                        <div className="flex justify-center py-20">
                            <Oval visible={true} height="50" width="50" color="#E04343" secondaryColor="#FCBF94" ariaLabel="oval-loading" />
                        </div>
                    ) : (
                        <>
                            {/* Desktop Table */}
                            <div className="hidden lg:block relative overflow-x-auto bg-white rounded-[10px] shadow-sm">
                                <table className="w-full text-sm text-left text-gray-500 border-separate border-spacing-y-2">
                                    <thead className="text-[12px] text-[#030229] bg-[#FAFAFA]">
                                        <tr>
                                            <th className="px-3 py-2">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIds.length === payments?.data?.length && payments?.data?.length > 0}
                                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                                    className="w-4 h-4 accent-[#E04343] rounded cursor-pointer"
                                                />
                                            </th>
                                            <th className="px-3 py-2">S/N</th>
                                            <th className="px-3 py-2">Shipment #</th>
                                            <th className="px-3 py-2">Rider</th>
                                            <th className="px-3 py-2">Fleet</th>
                                            <th className="px-3 py-2">Amount</th>
                                            <th className="px-3 py-2">Rate Type</th>
                                            <th className="px-3 py-2">Town</th>
                                            <th className="px-3 py-2">Status</th>
                                            <th className="px-3 py-2">Late Fine</th>
                                            <th className="px-3 py-2">Created</th>
                                            <th className="px-3 py-2">Confirmed By</th>
                                            <th className="px-3 py-2">Approved By</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {payments?.data?.length > 0 ? (
                                            payments.data.map((payment: any, index: number) => (
                                                <tr key={payment.id} className="bg-white hover:bg-gray-50">
                                                    <td className="px-3 py-3">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedIds.includes(payment.id)}
                                                            onChange={(e) => handleSelect(payment.id, e.target.checked)}
                                                            className="w-4 h-4 accent-[#E04343] rounded cursor-pointer"
                                                        />
                                                    </td>
                                                    <td className="px-3 py-3 text-[12px]">{((payments.current_page - 1) * perPage) + index + 1}</td>
                                                    <td className="px-3 py-3 text-[12px] font-[600] text-[#333333]">{payment.shipment_number || '—'}</td>
                                                    <td className="px-3 py-3">
                                                        <div>
                                                            <p className="text-[12px] font-[500] text-[#333333]">{payment.rider_first_name} {payment.rider_last_name || ''}</p>
                                                            <p className="text-[10px] text-[#808080]">{payment.rider_email}</p>
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-3 text-[12px]">
                                                        {payment.fleet_first_name ? `${payment.fleet_first_name} ${payment.fleet_last_name || ''}` : '—'}
                                                    </td>
                                                    <td className="px-3 py-3 text-[13px] font-[700] text-[#333333]">{formatCurrency(payment.amount)}</td>
                                                    <td className="px-3 py-3">
                                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-[600] ${payment.rate_type === 'metro' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                                                            {payment.rate_type || '—'}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-3 text-[12px]">{payment.rider_town || '—'}</td>
                                                    <td className="px-3 py-3">{getStatusBadge(payment.status)}</td>
                                                    <td className="px-3 py-3 text-[12px]">
                                                        {payment.late_fine_amount && Number(payment.late_fine_amount) > 0 ? (
                                                            <div>
                                                                <span className="text-red-600 font-[600]">-{formatCurrency(payment.late_fine_amount)}</span>
                                                                {payment.late_fine_reason && (
                                                                    <p className="text-[10px] text-[#808080] max-w-[120px] truncate" title={payment.late_fine_reason}>{payment.late_fine_reason}</p>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <span className="text-gray-300">—</span>
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-3 text-[11px] text-[#808080]">{formatDate(payment.created_at)}</td>
                                                    <td className="px-3 py-3">
                                                        {payment.confirmed_by_first_name ? (
                                                            <div>
                                                                <p className="text-[11px] font-[500] text-[#333333]">{payment.confirmed_by_first_name} {payment.confirmed_by_last_name || ''}</p>
                                                                <p className="text-[10px] text-[#808080]">{payment.confirmed_by_email}</p>
                                                                {payment.confirmed_at && <p className="text-[9px] text-[#AAAAAA]">{formatDate(payment.confirmed_at)}</p>}
                                                            </div>
                                                        ) : (
                                                            <span className="text-gray-300">—</span>
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-3">
                                                        {payment.approved_by_first_name ? (
                                                            <div>
                                                                <p className="text-[11px] font-[500] text-[#333333]">{payment.approved_by_first_name} {payment.approved_by_last_name || ''}</p>
                                                                <p className="text-[10px] text-[#808080]">{payment.approved_by_email}</p>
                                                                {payment.approved_at && <p className="text-[9px] text-[#AAAAAA]">{formatDate(payment.approved_at)}</p>}
                                                            </div>
                                                        ) : (
                                                            <span className="text-gray-300">—</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={13} className="text-center py-10 text-gray-400">No delivery payments found</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile Cards */}
                            <div className="lg:hidden space-y-3">
                                {payments?.data?.length > 0 ? (
                                    payments.data.map((payment: any, index: number) => (
                                        <div key={payment.id} className={`rounded-[10px] shadow-sm border p-4 ${selectedIds.includes(payment.id) ? 'bg-purple-50 border-purple-300' : 'bg-white'}`}>
                                            <div className="flex justify-between items-start mb-3">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIds.includes(payment.id)}
                                                    onChange={(e) => handleSelect(payment.id, e.target.checked)}
                                                    className="w-4 h-4 accent-[#E04343] rounded cursor-pointer mt-1 mr-2 flex-shrink-0"
                                                />
                                                <div>
                                                    <p className="text-[13px] font-[700] text-[#333333]">{payment.shipment_number || '—'}</p>
                                                    <p className="text-[11px] text-[#808080]">{payment.rider_first_name} {payment.rider_last_name || ''}</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-[15px] font-[700] text-[#333333]">{formatCurrency(payment.amount)}</p>
                                                    {getStatusBadge(payment.status)}
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2 text-[11px]">
                                                <div>
                                                    <span className="text-[#808080]">Rate Type:</span>{' '}
                                                    <span className={`font-[600] ${payment.rate_type === 'metro' ? 'text-blue-700' : 'text-purple-700'}`}>{payment.rate_type || '—'}</span>
                                                </div>
                                                <div>
                                                    <span className="text-[#808080]">Town:</span>{' '}
                                                    <span className="font-[500] text-[#333333]">{payment.rider_town || '—'}</span>
                                                </div>
                                                {payment.fleet_first_name && (
                                                    <div>
                                                        <span className="text-[#808080]">Fleet:</span>{' '}
                                                        <span className="font-[500] text-[#333333]">{payment.fleet_first_name}</span>
                                                    </div>
                                                )}
                                                {payment.late_fine_amount && Number(payment.late_fine_amount) > 0 && (
                                                    <div>
                                                        <span className="text-[#808080]">Fine:</span>{' '}
                                                        <span className="font-[600] text-red-600">-{formatCurrency(payment.late_fine_amount)}</span>
                                                    </div>
                                                )}
                                                <div className="col-span-2">
                                                    <span className="text-[#808080]">Created:</span>{' '}
                                                    <span className="text-[#333333]">{formatDate(payment.created_at)}</span>
                                                </div>
                                                {payment.confirmed_by_first_name && (
                                                    <div className="col-span-2">
                                                        <span className="text-[#808080]">Confirmed By:</span>{' '}
                                                        <span className="font-[500] text-[#333333]">{payment.confirmed_by_first_name} {payment.confirmed_by_last_name || ''}</span>
                                                        <span className="text-[#AAAAAA] ml-1">({payment.confirmed_by_email})</span>
                                                    </div>
                                                )}
                                                {payment.approved_by_first_name && (
                                                    <div className="col-span-2">
                                                        <span className="text-[#808080]">Approved By:</span>{' '}
                                                        <span className="font-[500] text-[#333333]">{payment.approved_by_first_name} {payment.approved_by_last_name || ''}</span>
                                                        <span className="text-[#AAAAAA] ml-1">({payment.approved_by_email})</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="bg-white rounded-[10px] p-8 text-center text-gray-400">No delivery payments found</div>
                                )}
                            </div>

                            {/* Pagination */}
                            <div className="m-4 flex justify-end mt-8">
                                {payments?.links?.map((link: any, index: number) => (
                                    <button
                                        key={index}
                                        onClick={() => {
                                            if (link.url) {
                                                const page = link.label === 'Next &raquo;'
                                                    ? payments.current_page + 1
                                                    : link.label === '&laquo; Previous'
                                                        ? payments.current_page - 1
                                                        : parseInt(link.label);
                                                paginator(page);
                                            }
                                        }}
                                        disabled={!link.url || link.active}
                                        className={'mx-1 py-1 px-2 rounded text-sm ' + (link.active ? 'bg-[#E04343] text-white' : link.url ? 'bg-gray-200 text-black hover:bg-gray-300' : 'bg-gray-100 text-gray-400')}
                                    >
                                        {link.label === '&laquo; Previous' ? '← Prev' : link.label === 'Next &raquo;' ? 'Next →' : link.label}
                                    </button>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>

            <ToastContainer position="top-right" autoClose={2000} hideProgressBar={true} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
        </>
    );
}

export default DeliveryPayments;